import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const FOREST = "#163A2A";
const MINT = "#8FC69B";
const CREAM = "#FAF7F2";

export default function ThankYou() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const firstName = state?.name ? state.name.trim().split(" ")[0] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="thankyou-page"
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: FOREST }}
    >
      {/* Minimal header */}
      <header className="px-6 sm:px-10 pt-7 pb-4 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex flex-col leading-none"
          aria-label="Go to home"
        >
          <span
            className="text-[9px] font-semibold uppercase tracking-[0.3em] mb-0.5"
            style={{ color: "rgba(143,198,155,0.7)" }}
          >
            Freshly
          </span>
          <span
            className="text-[1.55rem] leading-none tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: MINT }}
          >
            Flavrd.
          </span>
        </button>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center px-6 sm:px-10 lg:px-16 py-14 sm:py-20">
        <div className="w-full max-w-lg">

          {/* Check icon */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8 w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: MINT }}
          >
            <Check size={28} strokeWidth={3} color={FOREST} />
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="leading-[1.04] mb-4"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: "clamp(2.6rem, 8vw, 3.8rem)",
              color: CREAM,
            }}
            data-testid="thankyou-headline"
          >
            {firstName ? `You're in, ${firstName}.` : "You're in."}
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-sm sm:text-base leading-relaxed mb-5 max-w-sm"
            style={{ color: MINT }}
            data-testid="thankyou-tagline"
          >
            Thank you for choosing FLAVRD. We're grateful to have you as part of our journey.
          </motion.p>

          {/* Message */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.28 }}
            className="text-sm leading-relaxed mb-10"
            style={{ color: "rgba(250,247,242,0.65)" }}
            data-testid="thankyou-message"
          >
            We'll reach out the moment we're ready to ship.
          </motion.p>

          {/* Divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.55, delay: 0.34 }}
            className="origin-left mb-9"
            style={{ height: "1px", backgroundColor: "rgba(143,198,155,0.2)" }}
          />

          {/* What happens next */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <p
              className="text-[10px] font-bold uppercase tracking-[0.3em] mb-5"
              style={{ color: MINT }}
            >
              What happens next
            </p>
            <div className="flex flex-col gap-4">
              {[
                "We're crafting the first small batch, just for early-access members.",
                "We'll reach out to you for shipping and delivery details once we're live.",
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span
                    className="text-[11px] font-bold shrink-0 pt-0.5"
                    style={{ color: MINT }}
                  >
                    0{i + 1}
                  </span>
                  <span
                    className="text-sm leading-relaxed"
                    style={{ color: "rgba(250,247,242,0.58)" }}
                  >
                    {step}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.54 }}
            onClick={() => navigate("/")}
            data-testid="thankyou-home-btn"
            className="mt-12 inline-flex items-center gap-2 text-sm font-semibold tracking-wide transition-opacity hover:opacity-70"
            style={{ color: MINT }}
          >
            Continue exploring
            <ArrowRight size={14} strokeWidth={2.5} />
          </motion.button>
        </div>
      </main>
    </div>
  );
}
